import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useAuth } from "@/hooks/use-auth";
import { useLang } from "@/hooks/use-language";
import { supabase } from "@/integrations/supabase/client";
import { STORIES } from "@/lib/content";
import { ArrowLeft, Heart, Shield, Sparkles, Sun, BookOpen } from "lucide-react";

export const Route = createFileRoute("/_authenticated/app/parent/today")({
  component: ParentToday,
});

type Child = {
  id: string;
  name: string;
  avatar_emoji: string;
  age_tier: string;
  total_points: number;
  current_streak: number;
  last_active_at: string | null;
};

function ParentToday() {
  const { user } = useAuth();
  const { lang, dir } = useLang();
  const isAr = lang === "ar";
  const [children, setChildren] = useState<Child[]>([]);
  const [todayCount, setTodayCount] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) return;
    (async () => {
      const since = new Date(); since.setHours(0, 0, 0, 0);
      const [{ data: kids }, { data: evs }] = await Promise.all([
        supabase.from("children").select("id, name, avatar_emoji, age_tier, total_points, current_streak, last_active_at").order("created_at"),
        supabase.from("progress_events").select("id").gte("created_at", since.toISOString()),
      ]);
      setChildren((kids as Child[]) ?? []);
      setTodayCount(evs?.length ?? 0);
      setLoading(false);
    })();
  }, [user]);

  const hour = new Date().getHours();
  const greet = isAr
    ? hour < 12 ? "صباح الخير" : hour < 18 ? "مساء النور" : "مساء الخير"
    : hour < 12 ? "Good morning" : hour < 18 ? "Good afternoon" : "Good evening";

  // Pick a suggested story for the first child today
  const firstChild = children[0];
  const suggestion = firstChild ? STORIES.find((s) => s.tier === firstChild.age_tier) ?? STORIES[0] : null;

  return (
    <div dir={dir} className="px-5 py-6 max-w-2xl mx-auto pb-16">
      <Link to="/app/parent" className="flex items-center gap-2 text-sm font-bold text-foreground/60 mb-4 hover:text-foreground">
        <ArrowLeft className="size-4" /> {isAr ? "لوحة الوالدين" : "Parent dashboard"}
      </Link>

      {/* Greeting */}
      <div className="mb-6 animate-fade-in">
        <p className="font-display-en text-[10px] uppercase tracking-[0.3em] text-primary font-bold mb-1 flex items-center gap-1.5">
          <Sun className="size-3" /> {isAr ? "اليوم" : "Today"}
        </p>
        <h1 className={`${isAr ? "ar-display" : "font-display-en"} text-3xl md:text-4xl font-bold tracking-tight`}>
          {greet}{isAr ? "،" : ","} {isAr ? "كل شيء بخير" : "everything is okay"} 🌿
        </h1>
        <p className={`${isAr ? "ar-story" : ""} text-sm text-foreground/65 mt-2 max-w-md`}>
          {isAr
            ? "لقطة هادئة عن يوم عائلتك. لا أرقام مرهقة — فقط ما يهم."
            : "A calm snapshot of your family's day. No overwhelming metrics — just what matters."}
        </p>
      </div>

      {loading ? (
        <div className="space-y-3">
          {[0, 1, 2].map((i) => <div key={i} className="h-24 rounded-3xl bg-card border-2 border-clay/30 animate-pulse" />)}
        </div>
      ) : (
        <>
          {/* Reassuring trust banner */}
          <div className="bg-gradient-to-br from-secondary/10 to-primary/5 border-2 border-secondary/25 rounded-3xl p-4 mb-5 flex items-center gap-3">
            <div className="size-10 rounded-2xl bg-secondary/15 flex items-center justify-center text-secondary">
              <Shield className="size-5" />
            </div>
            <div className="flex-1">
              <p className="text-sm font-bold leading-tight">
                {isAr ? "آمن وبدون إعلانات اليوم" : "Safe and ad-free today"}
              </p>
              <p className="text-xs text-foreground/60 mt-0.5">
                {isAr ? "محتوى مُراجَع · بيئة محمية بـ PIN" : "Reviewed content · PIN-protected zone"}
              </p>
            </div>
          </div>

          {/* Today summary card */}
          <div className="bg-card border-2 border-clay/30 rounded-3xl p-5 mb-5">
            <div className="flex items-baseline justify-between mb-3">
              <h2 className={`${isAr ? "ar-display" : "font-display-en"} text-lg font-bold`}>
                {isAr ? "ماذا حدث اليوم" : "What happened today"}
              </h2>
              <span className="font-display-en text-2xl font-black text-primary tabular-nums">
                {todayCount}
              </span>
            </div>
            {children.length === 0 ? (
              <p className="text-sm text-foreground/60">
                {isAr ? "لم تتم إضافة أطفال بعد." : "No children added yet."}
              </p>
            ) : (
              <ul className="space-y-2">
                {children.map((c) => (
                  <li key={c.id} className="flex items-center gap-3 p-2.5 rounded-2xl hover:bg-clay/20 transition-colors">
                    <span className="text-2xl">{c.avatar_emoji}</span>
                    <div className="flex-1 min-w-0">
                      <p className={`${isAr ? "ar-display text-base" : "font-display-en text-sm"} font-bold truncate`}>{c.name}</p>
                      <p className="text-[11px] text-foreground/55">
                        ⭐ {c.total_points} · 🔥 {c.current_streak}d ·{" "}
                        {c.last_active_at
                          ? new Date(c.last_active_at).toLocaleDateString(isAr ? "ar" : "en", { weekday: "short" })
                          : isAr ? "لم يبدأ بعد" : "Not yet"}
                      </p>
                    </div>
                    <Link
                      to="/app/child/$childId"
                      params={{ childId: c.id }}
                      className="text-[11px] font-bold text-primary hover:underline shrink-0"
                    >
                      {isAr ? "افتح" : "Open"}
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* Suggestion of the day */}
          {suggestion && firstChild && (
            <div className="bg-gradient-to-br from-primary/12 via-accent/8 to-secondary/8 border-2 border-primary/25 rounded-3xl p-5 mb-5">
              <p className="font-display-en text-[10px] uppercase tracking-[0.28em] text-primary font-bold mb-2 flex items-center gap-1.5">
                <Sparkles className="size-3" /> {isAr ? "اقتراح هذا المساء" : "Tonight's suggestion"}
              </p>
              <div className="flex items-center gap-4">
                <div className={`size-16 rounded-2xl bg-gradient-to-br ${suggestion.cover} flex items-center justify-center text-3xl shadow-md`}>
                  {suggestion.emoji}
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className={`${isAr ? "ar-display" : "font-display-en"} text-base font-bold leading-tight`}>
                    {isAr ? suggestion.title_ar : suggestion.title_en}
                  </h3>
                  <p className={`${isAr ? "ar-story" : ""} text-xs text-foreground/65 mt-1 leading-relaxed`}>
                    {isAr
                      ? `قصة ${suggestion.duration} دقائق لقراءتها مع ${firstChild.name} قبل النوم.`
                      : `A ${suggestion.duration}-minute story to share with ${firstChild.name} tonight.`}
                  </p>
                </div>
              </div>
              <Link
                to="/app/child/$childId/stories"
                params={{ childId: firstChild.id }}
                className="mt-4 inline-flex items-center gap-2 px-4 py-2.5 rounded-2xl bg-primary text-primary-foreground text-sm font-bold hover:opacity-90 transition-opacity"
              >
                <BookOpen className="size-4" /> {isAr ? "ابدأ القصة" : "Begin story"}
              </Link>
            </div>
          )}

          {/* Warm closing note */}
          <div className="flex items-start gap-3 p-4 rounded-3xl bg-clay/15">
            <Heart className="size-4 text-primary fill-primary/30 mt-0.5 shrink-0" />
            <p className={`${isAr ? "ar-story" : ""} text-sm text-foreground/70 leading-relaxed`}>
              {isAr
                ? "كل دقيقة قضاها طفلك هنا هي خطوة صغيرة في رحلة طويلة. شكرًا لوجودك معنا."
                : "Every minute your child spends here is a small step on a long journey. Thank you for trusting us."}
            </p>
          </div>
        </>
      )}
    </div>
  );
}
